import { useQuery } from "@tanstack/react-query";
import { api, type Forecast } from "./api";
import { ErrorMessage, Loading } from "./components";
import { money, pct } from "./format";

const monthName = (f: Forecast) => new Date(`${f.monthStart}T00:00:00Z`).toLocaleDateString("en-US", { month: "long", timeZone: "UTC" });
const endLabel = (f: Forecast) => new Date(`${f.monthEnd}T00:00:00Z`).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });

/** This month so far, the pace, and where that pace ends the month. Estimates on estimates: says so. */
export function ForecastCard({ compact = false }: { compact?: boolean }) {
  const q = useQuery({ queryKey: ["forecast"], queryFn: api.forecast });
  if (q.isPending) return <forge-card><Loading /></forge-card>;
  if (q.error) return <ErrorMessage error={q.error} />;
  const f = q.data;
  const scale = Math.max(f.projectedHigh ?? 0, f.monthToDate, f.budget ?? 0) || 1;
  const w = (v: number) => `${Math.min(100, (v / scale) * 100)}%`;

  // On Home beside the cache card: one figure, the bar, one line. Until there is history, just the pace.
  if (compact) {
    return (
      <forge-card>
        <div className="stack gap-xsmall">
          <span className="hero__eyebrow"><forge-icon name="schedule" /> {monthName(f)}</span>
          <span className="stat-figure">
            {f.enoughHistory && f.projected !== null ? `heading for ${money(f.projected)}` : `${money(f.monthToDate)} so far`}
          </span>
          {f.enoughHistory ? (
            <>
              <div className="forecast-bar" role="img" aria-label={`Spent ${money(f.monthToDate)} of a projected ${money(f.projected ?? 0)} this month`}>
                <div className="forecast-bar__range" style={{ left: w(f.projectedLow ?? 0), width: `calc(${w(f.projectedHigh ?? 0)} - ${w(f.projectedLow ?? 0)})` }} />
                <div className="forecast-bar__projected" style={{ width: w(f.projected ?? 0) }} />
                <div className="forecast-bar__spent" style={{ width: w(f.monthToDate) }} />
                {f.budget !== null && <div className="forecast-bar__budget" style={{ left: w(f.budget) }} title={`Budget ${money(f.budget)}`} />}
              </div>
              <span className="forge-typography--body2">
                {money(f.monthToDate)} spent{f.pace7 !== null ? ` · ${money(f.pace7)}/day lately` : ""}
                {f.budget !== null && f.projectedBudgetShare !== null ? ` · ${pct(f.projectedBudgetShare)} of your ${money(f.budget)} budget by ${endLabel(f)}` : ""}
              </span>
            </>
          ) : (
            <span className="forge-typography--body2 muted">
              {f.pace7 !== null ? `${money(f.pace7)}/day lately. ` : ""}The month-end forecast starts after three full days of use.
              {f.budget !== null ? ` Your budget is ${money(f.budget)}.` : ""}
            </span>
          )}
        </div>
      </forge-card>
    );
  }

  return (
    <forge-card>
      <div className="stack gap-medium">
        <div className="row spread wrap gap-small">
          <h2 className="forge-typography--heading2 card-title">{monthName(f)} so far, and where it's heading</h2>
          <span className="muted">{pct(f.elapsed)} of the month gone · days are UTC</span>
        </div>

        <div className="forecast-figures">
          <div>
            <div className="muted forge-typography--label2">Spent this month</div>
            <div className="forge-typography--heading6">{money(f.monthToDate)}</div>
          </div>
          <div>
            <div className="muted forge-typography--label2">Projected by {endLabel(f)}</div>
            <div className="forge-typography--heading6">{f.projected === null ? "–" : money(f.projected)}</div>
            {f.projectedLow !== null && f.projectedHigh !== null && f.projectedHigh - f.projectedLow >= 0.01 && (
              <div className="muted">likely {money(f.projectedLow)} – {money(f.projectedHigh)}</div>
            )}
          </div>
          <div>
            <div className="muted forge-typography--label2">Daily pace, last 7 days</div>
            <div className="forge-typography--heading6">{f.pace7 === null ? "–" : `${money(f.pace7)}/day`}</div>
            {f.pace14 !== null && <div className="muted">{money(f.pace14)}/day over 14 days</div>}
          </div>
          {f.budget !== null && (
            <div>
              <div className="muted forge-typography--label2">Of your budget</div>
              <div className="forge-typography--heading6">{f.projectedBudgetShare === null ? "–" : pct(f.projectedBudgetShare)}</div>
              <div className="muted">projected, of {money(f.budget)}</div>
            </div>
          )}
        </div>

        {f.enoughHistory ? (
          <div className="forecast-bar" role="img"
            aria-label={`Spent ${money(f.monthToDate)} of a projected ${money(f.projected ?? 0)} this month`}>
            <div className="forecast-bar__range" style={{ left: w(f.projectedLow ?? 0), width: `calc(${w(f.projectedHigh ?? 0)} - ${w(f.projectedLow ?? 0)})` }} />
            <div className="forecast-bar__projected" style={{ width: w(f.projected ?? 0) }} />
            <div className="forecast-bar__spent" style={{ width: w(f.monthToDate) }} />
            {f.budget !== null && <div className="forecast-bar__budget" style={{ left: w(f.budget) }} title={`Budget ${money(f.budget)}`} />}
          </div>
        ) : (
          <p className="forge-typography--body1 muted">
            Not enough history to project yet: the forecast starts after three full days of use.
          </p>
        )}
        <p className="forge-typography--body1 muted">
          The projection assumes the last seven days' pace continues; the range uses the 7- and 14-day paces. It is an
          estimate built on LiteLLM's list-price estimates, not a bill.
        </p>
      </div>
    </forge-card>
  );
}
